(() => {
  if (window.ARIStorefrontLoaded) return;
  window.ARIStorefrontLoaded = true;

  const emit = (name, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });

    if (typeof window.clarity === 'function') {
      window.clarity('event', name);
    }

    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  window.ARIStorefront = window.ARIStorefront || {};
  window.ARIStorefront.emit = emit;

  const normalize = (input = '') =>
    String(input)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-zA-Z0-9]+/g, ' ')
      .trim()
      .toLowerCase();

  const genreAliases = {
    'afro house': ['afro house', 'afrohouse'],
    'dance pop': ['dance pop', 'pop dance'],
    'deep house': ['deep house', 'deephouse'],
    'drum and bass': ['drum and bass', 'drum n bass', 'dnb'],
    dubstep: ['dubstep'],
    edm: ['edm', 'electronic dance music'],
    house: ['house'],
    'melodic house': ['melodic house'],
    'melodic techno': ['melodic techno'],
    pop: ['pop'],
    'tech house': ['tech house', 'techhouse'],
    techno: ['techno'],
    trance: ['trance'],
  };

  const rowMatchesGenre = (row, selectedGenre) => {
    if (!selectedGenre) return true;
    const haystack = normalize(row.dataset.genre);
    const aliases = genreAliases[selectedGenre] || [selectedGenre];
    return aliases.some((alias) => haystack.includes(alias));
  };

  const rowMatchesAnyGenre = (row, selectedGenres = []) =>
    selectedGenres.length === 0 || selectedGenres.some((selected) => rowMatchesGenre(row, selected));

  const rowMatchesAnyLicense = (row, selectedLicenses = []) =>
    selectedLicenses.length === 0 || selectedLicenses.includes(normalize(row.dataset.license));

  const vocalTypeToLicense = {
    'non exclusive': 'non-exclusive',
    'limited edition': 'limited',
    'exclusive vocal': 'exclusive',
  };

  const intentLabels = {
    'afro house': 'Afro House',
    'deep house': 'Deep House',
    'drum and bass': 'Drum & Bass',
    'melodic house': 'Melodic House',
    'non exclusive': 'Non-exclusive',
    'tech house': 'Tech House',
    dubstep: 'Dubstep',
    exclusive: 'Exclusive vocal',
    house: 'House',
    limited: 'Limited edition',
    pop: 'Pop',
    techno: 'Techno',
    trance: 'Trance',
  };

  const intentLabel = (item) =>
    intentLabels[item] || item.replace(/\b\w/g, (character) => character.toUpperCase());

  const initAudioTracking = (player) => {
    if (player.dataset.ariTrackingReady === 'true') return;
    player.dataset.ariTrackingReady = 'true';

    const audio = player.querySelector('audio');
    const range = player.querySelector('[data-audio-range]');
    if (!audio) return;

    const updateProgressColor = () => {
      if (!range) return;
      const progress = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
      range.style.setProperty('--ari-progress', `${progress}%`);
    };

    audio.addEventListener('loadedmetadata', updateProgressColor);
    audio.addEventListener('timeupdate', updateProgressColor);
    audio.addEventListener('play', () => {
      emit('ari_audio_play', {
        product_id: player.dataset.productId,
        product_title: player.dataset.productTitle,
      });
    });
    audio.addEventListener('ended', () => {
      updateProgressColor();
      emit('ari_audio_complete', {
        product_id: player.dataset.productId,
        product_title: player.dataset.productTitle,
      });
    });
  };

  const initCatalog = (catalog) => {
    if (catalog.dataset.ariReady === 'true') return;
    catalog.dataset.ariReady = 'true';

    const list = catalog.querySelector('[data-ari-list]');
    const rows = Array.from(catalog.querySelectorAll('[data-ari-product-row]'));
    const search = catalog.querySelector('[data-ari-search]');
    const mobileSearch = catalog.querySelector('[data-ari-mobile-search]');
    const genre = catalog.querySelector('[data-ari-genre]');
    const genreField = catalog.querySelector('[data-ari-genre-field]');
    const license = catalog.querySelector('[data-ari-license]');
    const sort = catalog.querySelector('[data-ari-sort]');
    const reset = catalog.querySelector('[data-ari-reset]');
    const count = catalog.querySelector('[data-ari-result-count]');
    const empty = catalog.querySelector('[data-ari-empty]');
    const emptyTitle = catalog.querySelector('[data-ari-empty-title]');
    const emptyCopy = catalog.querySelector('[data-ari-empty-copy]');
    const loadMore = catalog.querySelector('[data-ari-load-more]');
    const filterToggle = catalog.querySelector('[data-ari-filter-toggle]');
    const filterPanel = catalog.querySelector('[data-ari-filter-panel]');
    const filterCount = catalog.querySelector('[data-ari-filter-count]');
    const surveyFilterChips = catalog.querySelector('[data-ari-survey-filter-chips]');
    const customMatch = catalog.querySelector('[data-ari-custom-match]');
    const step = Math.max(1, Number(catalog.dataset.loadStep) || 6);
    let visibleLimit = Math.max(1, Number(catalog.dataset.initialVisible) || rows.length);
    let lastFilterSignature = '';
    let surveyGenres = [];
    let surveyLicenses = [];
    let customRequested = false;
    let intentApplied = false;
    let noMatchTracked = false;

    const defaultEmptyTitle = emptyTitle?.textContent || '';
    const defaultEmptyCopy = emptyCopy?.textContent || '';
    const mobileViewport =
      typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 749px)') : null;

    const value = (element) => normalize(element?.value);

    const renderSurveyFilters = () => {
      if (customMatch) customMatch.hidden = !customRequested;
      if (!surveyFilterChips) return;

      surveyFilterChips.replaceChildren();
      const selections = [
        ...surveyGenres.map((item) => ({ dimension: 'genre', item })),
        ...surveyLicenses.map((item) => ({ dimension: 'license', item })),
      ];

      selections.forEach(({ dimension, item }) => {
        const label = intentLabel(item);
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.dataset.ariSurveyFilter = dimension;
        chip.dataset.value = item;
        chip.setAttribute('aria-label', `Remove ${label} filter`);
        chip.textContent = `${label} x`;
        surveyFilterChips.appendChild(chip);
      });

      surveyFilterChips.hidden = selections.length === 0;
    };

    const updateFilterCount = (activeGenres, activeLicenses) => {
      if (!filterCount) return;
      const activeCount = activeGenres.length + activeLicenses.length;
      filterCount.textContent = String(activeCount);
      filterCount.hidden = activeCount === 0;
    };

    const updateGenreAvailability = () => {
      if (!genre || !genreField) return;

      let usableOptions = 0;
      genre.querySelectorAll('[data-ari-genre-option]').forEach((option) => {
        const label = option.dataset.label || option.textContent.trim();
        const optionValue = normalize(option.value);
        const productCount = rows.filter((row) => rowMatchesGenre(row, optionValue)).length;
        const available = productCount > 0;

        option.disabled = !available;
        option.hidden = !available;
        option.textContent = available ? `${label} (${productCount})` : label;
        if (available) usableOptions += 1;
      });

      genreField.hidden = usableOptions === 0;
      if (genre.selectedOptions[0]?.disabled) genre.value = '';
    };

    const sortRows = (mode = value(sort)) => {
      const sorted = [...rows];

      if (mode === 'featured') {
        sorted.sort((a, b) => {
          const aPinned = Number(a.dataset.pinnedPriority) || 0;
          const bPinned = Number(b.dataset.pinnedPriority) || 0;
          if (aPinned && bPinned) return aPinned - bPinned;
          if (aPinned) return -1;
          if (bPinned) return 1;
          return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
        });
      } else if (mode === 'title') {
        sorted.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title));
      } else if (mode === 'price-low') {
        sorted.sort((a, b) => Number(a.dataset.price) - Number(b.dataset.price));
      } else if (mode === 'price-high') {
        sorted.sort((a, b) => Number(b.dataset.price) - Number(a.dataset.price));
      }

      updateRowNumbers(sorted);
      sorted.forEach((row) => list.appendChild(row));
      return sorted;
    };

    const updateRowNumbers = (sortedRows) => {
      sortedRows.forEach((row, index) => {
        const number = row.querySelector('[data-ari-row-number]');
        if (number) number.textContent = String(index + 1).padStart(2, '0');
        row.style.setProperty('--ari-row-index', index + 1);
      });
    };

    const refresh = ({ track = false, source = 'manual' } = {}) => {
      const query = value(search || mobileSearch);
      const manualGenre = value(genre);
      const manualLicense = value(license);
      const activeGenres = manualGenre ? [manualGenre] : surveyGenres;
      const activeLicenses = manualLicense ? [manualLicense] : surveyLicenses;
      const signature = `${query}|${activeGenres.join(',')}|${activeLicenses.join(',')}|${customRequested}`;

      if (signature !== lastFilterSignature) visibleLimit = Math.max(1, Number(catalog.dataset.initialVisible) || rows.length);
      lastFilterSignature = signature;

      const sortedRows = sortRows();
      const queryTerms = query.split(' ').filter(Boolean);
      const matchingRows = sortedRows.filter((row) => {
        const searchText = normalize(row.dataset.search);
        const matchesSearch = queryTerms.length === 0 || queryTerms.every((term) => searchText.includes(term));
        const matchesGenre = rowMatchesAnyGenre(row, activeGenres);
        const matchesLicense = rowMatchesAnyLicense(row, activeLicenses);
        return matchesSearch && matchesGenre && matchesLicense;
      });

      const matching = matchingRows.length;
      const isSurveyApplication = intentApplied && source === 'intent-survey';
      const hasSpecificSurveyFilters = surveyGenres.length > 0 || surveyLicenses.length > 0;
      const fallback = isSurveyApplication && hasSpecificSurveyFilters && matching === 0;
      const matchType = fallback ? 'fallback' : activeGenres.length > 0 || activeLicenses.length > 0 ? 'exact' : 'broad';
      const displayRows = fallback ? sortRows('featured') : matchingRows;
      const visibleRows = new Set(displayRows.slice(0, visibleLimit));

      rows.forEach((row) => {
        row.hidden = !visibleRows.has(row);
      });

      if (count) count.textContent = `${matching} vocal${matching === 1 ? '' : 's'}`;
      if (empty) empty.hidden = fallback ? false : matching !== 0;
      if (emptyTitle) {
        emptyTitle.textContent = fallback
          ? 'No exact matches yet. Here are the current bestsellers.'
          : defaultEmptyTitle;
      }
      if (emptyCopy) {
        emptyCopy.textContent = fallback ? '' : defaultEmptyCopy;
        emptyCopy.hidden = fallback;
      }
      if (loadMore) loadMore.hidden = displayRows.length <= visibleLimit;

      renderSurveyFilters();
      updateFilterCount(activeGenres, activeLicenses);

      if (fallback && !noMatchTracked) {
        noMatchTracked = true;
        emit('ari_intent_no_match', {
          genres: surveyGenres.join(', '),
          licenses: surveyLicenses.join(', '),
          source,
        });
      }

      if (track) {
        emit('ari_catalog_filter', {
          query,
          genre: activeGenres.join(', ') || 'all',
          license: activeLicenses.join(', ') || 'all',
          sort: value(sort) || 'featured',
          results: matching,
        });
      }

      return { matching, fallback, matchType };
    };

    genre?.addEventListener('change', () => {
      surveyGenres = [];
      refresh({ track: true });
    });

    license?.addEventListener('change', () => {
      surveyLicenses = [];
      refresh({ track: true });
    });

    sort?.addEventListener('change', () => refresh({ track: true }));

    let searchTimer;
    const scheduleSearchRefresh = (sourceInput, mirrorInput) => {
      if (mirrorInput && mirrorInput.value !== sourceInput.value) mirrorInput.value = sourceInput.value;
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => refresh({ track: value(sourceInput).length > 1 }), 160);
    };

    search?.addEventListener('input', () => scheduleSearchRefresh(search, mobileSearch));
    mobileSearch?.addEventListener('input', () => scheduleSearchRefresh(mobileSearch, search));

    reset?.addEventListener('click', () => {
      if (search) search.value = '';
      if (mobileSearch) mobileSearch.value = '';
      if (genre) genre.value = '';
      if (license) license.value = '';
      if (sort) sort.value = 'featured';
      surveyGenres = [];
      surveyLicenses = [];
      customRequested = false;
      intentApplied = false;
      noMatchTracked = false;
      lastFilterSignature = '';
      refresh({ track: true });
    });

    if (filterToggle && filterPanel) {
      const setFilterPanelOpen = (open) => {
        filterPanel.hidden = !open;
        filterToggle.setAttribute('aria-expanded', String(open));
      };

      setFilterPanelOpen(!mobileViewport?.matches);
      filterToggle.addEventListener('click', () => setFilterPanelOpen(filterPanel.hidden));

      const handleViewportChange = (event) => setFilterPanelOpen(!event.matches);
      if (typeof mobileViewport?.addEventListener === 'function') {
        mobileViewport.addEventListener('change', handleViewportChange);
      } else if (typeof mobileViewport?.addListener === 'function') {
        mobileViewport.addListener(handleViewportChange);
      }
    }

    surveyFilterChips?.addEventListener('click', (event) => {
      const chip = event.target.closest('button[data-ari-survey-filter]');
      if (!chip || !surveyFilterChips.contains(chip)) return;

      if (chip.dataset.ariSurveyFilter === 'genre') {
        surveyGenres = surveyGenres.filter((item) => item !== chip.dataset.value);
      } else {
        surveyLicenses = surveyLicenses.filter((item) => item !== chip.dataset.value);
      }

      refresh({ track: true, source: 'intent-chip' });
    });

    catalog.addEventListener('ari:intent:apply', (event) => {
      const genres = Array.isArray(event.detail?.genres) ? event.detail.genres.map(normalize) : [];
      const vocalTypes = Array.isArray(event.detail?.vocalTypes) ? event.detail.vocalTypes.map(normalize) : [];

      surveyGenres = genres.filter((item) => !['edm', 'other', 'i dont know yet'].includes(item));
      surveyLicenses = vocalTypes.map((item) => vocalTypeToLicense[item]).filter(Boolean).map(normalize);
      customRequested = vocalTypes.includes('custom vocal');
      intentApplied = true;
      noMatchTracked = false;

      const result = refresh({ track: true, source: 'intent-survey' });
      catalog.dispatchEvent(new CustomEvent('ari:intent:result', { detail: result }));
    });

    rows.forEach((row) => {
      row.addEventListener('click', (event) => {
        const isMobile = mobileViewport ? mobileViewport.matches : window.innerWidth <= 749;
        if (!isMobile || event.defaultPrevented) return;
        if (event.target.closest('a, button, input, select, textarea, label, [role="button"]')) return;

        const titleLink = row.querySelector('.ari-vocal-row__title a');
        if (titleLink?.href) window.location.assign(titleLink.href);
      });
    });

    loadMore?.addEventListener('click', () => {
      visibleLimit += step;
      refresh();
      emit('ari_catalog_load_more', { visible_limit: visibleLimit });
    });

    catalog.querySelectorAll('[data-ari-cart-form]').forEach((formRoot) => {
      const form = formRoot.matches('form') ? formRoot : formRoot.querySelector('form');
      if (!form) return;
      form.addEventListener('submit', () => {
        emit('ari_add_to_cart_intent', {
          product_id: formRoot.dataset.productId,
          product_title: formRoot.dataset.productTitle,
        });
      });
    });

    updateGenreAvailability();
    refresh();
  };

  const initDemandForm = (form) => {
    if (form.dataset.ariReady === 'true') return;
    form.dataset.ariReady = 'true';

    const params = new URLSearchParams(window.location.search);
    form.querySelectorAll('[data-ari-attribution]').forEach((field) => {
      const key = field.dataset.ariAttribution;
      if (key === 'landing_page') field.value = window.location.href;
      else field.value = params.get(key) || '';
    });

    form.addEventListener('submit', () => {
      const selectedGenres = Array.from(form.querySelectorAll('input[data-ari-genre-chip]:checked')).map(
        (input) => input.value,
      );
      emit('ari_vocal_request_submit', {
        genres: selectedGenres.join(', '),
        bpm: form.querySelector('[name="contact[BPM range]"]')?.value || '',
        vibe: form.querySelector('[name="contact[Vibe]"]')?.value || '',
        license: form.querySelector('[name="contact[Preferred license]"]')?.value || '',
      });
    });
  };

  const initContact = (contact) => {
    if (contact.dataset.ariReady === 'true') return;
    contact.dataset.ariReady = 'true';

    const launcher = contact.querySelector('[data-ari-contact-launcher]');
    const panel = contact.querySelector('[data-ari-contact-panel]');
    const close = contact.querySelector('[data-ari-contact-close]');
    if (!launcher || !panel) return;

    const setOpen = (open) => {
      panel.hidden = !open;
      launcher.setAttribute('aria-expanded', String(open));
      if (open) {
        close?.focus({ preventScroll: true });
        emit('ari_contact_open');
      }
    };

    launcher.addEventListener('click', () => setOpen(panel.hidden));
    close?.addEventListener('click', () => setOpen(false));
    contact.querySelectorAll('[data-ari-contact-link]').forEach((link) => {
      link.addEventListener('click', () => {
        emit('ari_contact_channel', { channel: link.dataset.ariContactLink });
        setOpen(false);
      });
    });

    document.addEventListener('click', (event) => {
      if (!panel.hidden && !contact.contains(event.target)) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        setOpen(false);
        launcher.focus({ preventScroll: true });
      }
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-ari-audio-track]').forEach(initAudioTracking);
    root.querySelectorAll('[data-ari-catalog]').forEach(initCatalog);
    root.querySelectorAll('[data-ari-demand-form]').forEach(initDemandForm);
    root.querySelectorAll('[data-ari-contact]').forEach(initContact);

    root.querySelectorAll('[data-ari-trial-download]').forEach((link) => {
      if (link.dataset.ariReady === 'true') return;
      link.dataset.ariReady = 'true';
      link.addEventListener('click', () => {
        emit('ari_trial_download', {
          product_id: link.dataset.productId,
          product_title: link.dataset.productTitle,
          location: link.dataset.location,
        });
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
